import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { chipStatusText, type Chip } from '../lib/attachments'
import { AttachmentModal } from './AttachmentModal'
import { fullPreviewKindOf } from './previewKind'
import type { PreviewItem } from './types'

/**
 * Composer'daki bekleyen ek chip'leri.
 *
 * Durumlar: `queued → uploading(%n) → processing → ready | error`.
 * `processing` ayri bir durum olmak ZORUNDA: baytlar gitti ama belge cikarimi
 * (PyMuPDF / LibreOffice) saniyeler surebiliyor ve yuzdeyi %100'de dondurup
 * birakmak kullaniciya uygulamanin takildigini dusundururdu.
 *
 * Chip'e tiklamak onizlemeyi aciyor - kullanici GONDERMEDEN once yukledigi
 * dosyayi dogrulayabiliyor.
 */
export function ChipList({
  chips,
  onRemove,
  onRetry,
}: {
  chips: Chip[]
  onRemove(localId: string): void
  onRetry(localId: string): void
}) {
  const { t } = useTranslation()
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  if (chips.length === 0) return null

  return (
    <>
      <ul className="chips">
        {chips.map((chip, index) => (
          <li key={chip.localId} className={`chip chip-${chip.state}`}>
            <button
              type="button"
              className="chip-open"
              onClick={() => setOpenIndex(index)}
              // Yuklenmemis bir dosyanin onizlemesi yalnizca yerel; hazir
              // olmayanlarda da acilabiliyor cunku kullanici "dogru dosyayi
              // mi sectim" sorusunu HEMEN sormak istiyor.
              title={t('attach.chip.preview')}
            >
              <span className="chip-name">{chip.file.name}</span>
              <span className="chip-state">{chipStatusText(chip)}</span>
            </button>

            {chip.state === 'uploading' && (
              <span
                className="chip-bar"
                role="progressbar"
                aria-valuenow={chip.progress}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={t('attach.chip.uploading', { name: chip.file.name })}
                style={{ '--pct': `${chip.progress}%` } as React.CSSProperties}
              />
            )}

            {chip.state === 'error' && (
              <button type="button" className="chip-retry" onClick={() => onRetry(chip.localId)}>
                {t('attach.chip.retry')}
              </button>
            )}
            <button
              type="button"
              className="chip-remove"
              onClick={() => onRemove(chip.localId)}
              aria-label={t('attach.chip.remove', { name: chip.file.name })}
            >
              ×
            </button>
          </li>
        ))}
      </ul>

      {openIndex !== null && (
        <ChipPreview chips={chips} startIndex={openIndex} onClose={() => setOpenIndex(null)} />
      )}
    </>
  )
}

/**
 * Bekleyen chip'lerin onizlemesi.
 *
 * Kaynak yerel `File`: `URL.createObjectURL` ile URL uretiliyor ve modal
 * kapaninca `revokeObjectURL` ile SERBEST BIRAKILIYOR. Birakmamak, dosyayi
 * sayfa omru boyunca bellekte tutmak demek - 20 MB'lik bir PDF'te bu
 * gorulur.
 */
function ChipPreview({
  chips,
  startIndex,
  onClose,
}: {
  chips: Chip[]
  startIndex: number
  onClose(): void
}) {
  const items = useMemo<PreviewItem[]>(
    () =>
      chips.map((chip) => {
        const url = URL.createObjectURL(chip.file)
        const uploaded = chip.uploaded
        // Tam onizleme SUNUCU ucundan: blob URL'ini iframe'de gostermek sayfa
        // CSP'sine `frame-src blob:` eklemeyi gerektirirdi. Yuklenmemis
        // chip'te henuz yok.
        const fullPreviewKind = uploaded
          ? fullPreviewKindOf(chip.file.name, chip.file.type || uploaded.mime)
          : null
        return {
          fullPreviewKind: fullPreviewKind ?? undefined,
          fullPreviewUrl:
            uploaded && fullPreviewKind ? `/api/attachments/${uploaded.id}/preview` : undefined,
          kind: uploaded?.kind ?? (chip.file.type.startsWith('image/') ? 'image' : 'document'),
          filename: chip.file.name,
          mime: chip.file.type || 'application/octet-stream',
          byteSize: chip.file.size,
          previewUrl: url,
          downloadUrl: url,
          width: uploaded?.width ?? null,
          height: uploaded?.height ?? null,
          // Yuklenmis bir chip'in sunucu detayi VAR: cikarilan metin ve
          // sayfa goruntuleri gosterilebiliyor.
          detailUrl: uploaded ? `/api/attachments/${uploaded.id}/detail` : undefined,
        }
      }),
    [chips],
  )

  // Blob URL'lerini serbest birak.
  useEffect(() => {
    return () => {
      for (const item of items) {
        if (item.previewUrl) URL.revokeObjectURL(item.previewUrl)
      }
    }
  }, [items])

  return <AttachmentModal items={items} startIndex={startIndex} onClose={onClose} />
}
